import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal, discounts, discountTotal, total } = useCart();
  const navigate = useNavigate();

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/70 z-50" onClick={() => setOpen(false)} />}
      <aside className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#111114] border-l border-white/10 z-50 transform transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="font-mono-label text-[11px] text-neutral-400">YOUR CART</div>
          <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto max-h-[calc(100vh-220px)] px-6 py-6">
          {items.length === 0 && (
            <div className="text-center mt-16">
              <div className="font-serif-display text-2xl text-white mb-2">Empty bench</div>
              <p className="text-neutral-500 text-sm">Nothing catalogued yet.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="inline-block mt-6 btn-pink-outline px-6 py-3 font-mono-label text-[11px]">BROWSE CATALOG</Link>
            </div>
          )}
          {items.map(item => (
            <div key={item.key} className="flex gap-4 mb-6 pb-6 border-b border-white/5">
              <div className="w-20 h-20 rounded-md overflow-hidden bg-black/50 shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="font-serif-display text-white text-lg">{item.name}</div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <div className="font-mono-label text-[10px] text-neutral-500">{item.dose}</div>
                  {item.preorder && (
                    <span className="font-mono-label text-[9px] px-1.5 py-0.5 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">PRE-ORDER</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 border border-white/10 rounded">
                    <button onClick={() => updateQty(item.key, item.qty - 1)} className="px-2 py-1 text-neutral-400 hover:text-white"><Minus size={12} /></button>
                    <span className="px-2 text-sm text-white">{item.qty}</span>
                    <button onClick={() => updateQty(item.key, item.qty + 1)} className="px-2 py-1 text-neutral-400 hover:text-white"><Plus size={12} /></button>
                  </div>
                  <div className="text-white text-sm">${(item.price * item.qty).toFixed(2)}</div>
                </div>
                <button onClick={() => removeItem(item.key)} className="mt-2 font-mono-label text-[10px] text-neutral-500 hover:text-[#ff2fa1]">REMOVE</button>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10 bg-[#111114] max-h-[calc(100vh-100px)] overflow-y-auto">
            {discounts && discounts.length > 0 && (
              <div className="mb-3 space-y-1" data-testid="cart-discount-block">
                <div className="flex items-center justify-between text-[12px] text-neutral-400">
                  <span className="font-mono-label text-[10px]">SUBTOTAL</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {discounts.map((d, i) => (
                  <div key={i} className="flex items-start justify-between text-[12px] text-[#ff2fa1]" data-testid={`cart-discount-${d.code}`}>
                    <span className="pr-3 leading-snug">{d.description}</span>
                    <span className="whitespace-nowrap">− ${d.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono-label text-[11px] text-neutral-400">{discounts && discounts.length > 0 ? 'TOTAL' : 'SUBTOTAL'}</span>
              <span className="font-serif-display text-2xl text-white" data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <button
              onClick={() => { setOpen(false); navigate('/checkout'); }}
              className="btn-pink w-full py-3 rounded font-mono-label text-[11px]">
              PROCEED TO CHECKOUT
            </button>
            <div className="mt-4 p-3 border border-[#ff2fa1]/20 bg-[#ff2fa1]/[0.04] rounded">
              <div className="font-mono-label text-[10px] text-[#ff2fa1] mb-1">RESEARCH USE ONLY</div>
              <p className="text-[11px] text-neutral-400 leading-relaxed">All products sold by Vial Rat Research Co. are intended strictly for laboratory and research purposes and are not intended for human consumption or medical use. By completing your purchase, you acknowledge and agree to these terms.</p>
            </div>
            <div className="mt-2 p-3 border border-white/10 rounded">
              <div className="font-mono-label text-[10px] text-neutral-300 mb-1">FINAL SALE</div>
              <p className="text-[11px] text-neutral-500 leading-relaxed">Due to the nature of our products, all sales are final. We do not accept returns or offer refunds except where required by applicable law. If your order arrives damaged or incorrect, please contact us so we can review the issue.</p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
